// The texts of the weather station and reset counter widgets (VisionPlatform specs/weather-station-widgets WSW-04,
// WSW-12, WSW-23, WSW-24). The fixtures' expected texts are produced by these rules (strings.json's description).
import type { PointState } from "./generated/widget-fixture";
import { formatValue } from "./format";
import { dayMonth, localDate } from "./statistics";

type T = (key: string, options?: Record<string, unknown>) => string;

const SECTORS = ["n", "ne", "e", "se", "s", "sw", "w", "nw"] as const;

/** The 45° sector the wind comes from, centred on its letter; a half rounds clockwise (22.5° is NE). */
export function compassSector(deg: number): (typeof SECTORS)[number] {
  const d = ((deg % 360) + 360) % 360;
  return SECTORS[Math.floor(d / 45 + 0.5) % 8] ?? "n";
}

const SPOKEN: Record<string, string> = {
  "°C": "degC",
  "%": "percent",
  mm: "mm",
  hPa: "hPa",
  "m/s": "ms",
  "km/h": "kmh",
  "m³": "m3",
};

/** A value for a screen reader: its number, a space and the unit in words ("18.4 degrees"). */
export function spokenValue(
  value: number,
  point: Pick<PointState, "unit" | "decimals">,
  locale: string,
  t: T,
  decimals = point.decimals,
): string {
  const n = formatValue(value, null, decimals, locale);
  if (!point.unit) return n;
  const k = SPOKEN[point.unit];
  return `${n} ${k ? t(`spoken.${k}`) : point.unit}`;
}

/** The day and month in the organisation's time zone, with the year when it is not this year ("3 October"). */
export function counterDate(at: string, now: Date, t: T, tz?: string): string {
  return dayMonth(localDate(Date.parse(at), tz), t, localDate(now.getTime(), tz).year);
}

/** "Rain since 3 October" for the rain trait, else "<value name> since 3 October" (WSW-24). */
export function counterTitle(
  point: Pick<PointState, "name" | "trait">,
  since: string,
  now: Date,
  t: T,
  tz?: string,
): string {
  const date = counterDate(since, now, t, tz);
  return point.trait === "rain" ? t("counter.rainSince", { date }) : t("counter.since", { name: point.name, date });
}

/** A period a reset ended: "3 to 28 October", "12 September to 3 October" (statistics.days with counter dates). */
export function counterSpan(from: string, to: string, now: Date, t: T, tz?: string): string {
  const year = localDate(now.getTime(), tz).year;
  const a = localDate(Date.parse(from), tz);
  const b = localDate(Date.parse(to), tz);
  const end = dayMonth(b, t, year);
  if (a.year === b.year && a.month === b.month && a.day === b.day) return end;
  const start = a.year !== b.year ? dayMonth(a, t, year) : a.month !== b.month ? dayMonth(a, t) : String(a.day);
  return t("statistics.days", { from: start, to: end });
}
